import { useState } from "react";
import { Nav, Footer, GlobalStyles, PageHeader, theme } from "./design-system";

// ── Shared helpers ────────────────────────────────────────────────────────────
function Section({ title, children, accent }) {
  return (
    <div style={{ background: theme.surface, borderRadius: theme.radius, padding: "1.6rem", border: `1px solid ${theme.border}`, borderTop: `3px solid ${accent || theme.clay}`, boxShadow: theme.shadow, marginBottom: "1.1rem" }}>
      <h2 style={{ fontSize: "0.7rem", fontWeight: "700", color: accent || theme.clay, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 1rem" }}>{title}</h2>
      {children}
    </div>
  );
}

function P({ children }) {
  return <p style={{ color: theme.textMid, lineHeight: 1.75, margin: "0 0 0.7rem", fontSize: "0.9rem" }}>{children}</p>;
}

function Bullets({ items, color }) {
  const c = color || theme.clay;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.5rem" }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: "flex", gap: "0.65rem" }}>
          <div style={{ width: "15px", height: "15px", borderRadius: "50%", background: c + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "3px" }}>
            <span style={{ color: c, fontSize: "0.5rem", fontWeight: "800" }}>✓</span>
          </div>
          <p style={{ margin: 0, fontSize: "0.87rem", color: theme.textMid, lineHeight: 1.65 }}>{item}</p>
        </div>
      ))}
    </div>
  );
}

function RedBox({ children }) {
  return (
    <div style={{ background: theme.redLight, borderRadius: theme.radius, padding: "1.4rem 1.5rem", border: `1px solid ${theme.red}33`, marginBottom: "1.1rem" }}>
      {children}
    </div>
  );
}

// ── ABOUT ─────────────────────────────────────────────────────────────────────
export function AboutPage() {
  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav active="about" />
      <PageHeader eyebrow="About" title="About CareNav" subtitle="AI-powered symptom guidance for non-emergency health concerns — designed with safety and clarity at its core." />
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "2.5rem 2rem" }}>

        <RedBox>
          <h2 style={{ fontSize: "0.7rem", fontWeight: "700", color: theme.red, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 0.75rem" }}>⚠ What CareNav is NOT</h2>
          {["CareNav does not and cannot diagnose medical conditions.", "CareNav is not a replacement for professional medical advice or treatment.", "CareNav is not an emergency service. In a life-threatening situation, call 999 immediately.", "CareNav is not affiliated with or endorsed by NHS England or any NHS organisation.", "CareNav does not prescribe medication or recommend prescription treatments."].map((item, i) => (
            <div key={i} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.35rem" }}>
              <span style={{ color: theme.red }}>•</span>
              <p style={{ margin: 0, fontSize: "0.85rem", color: theme.red, lineHeight: 1.6 }}>{item}</p>
            </div>
          ))}
        </RedBox>

        <Section title="What is CareNav?">
          <P>CareNav is a web-based AI self-triage system developed as part of a university research project at the University of Chester (CO6008 Implementation Project, 2025–2026). It helps members of the public assess non-emergency symptoms, understand common medical conditions, and navigate to the most appropriate level of NHS care.</P>
          <P>The system covers 60 common non-emergency conditions across nine symptom categories, and uses Google Gemini AI to have a natural conversation with users before matching their symptoms to pre-written, clinically informed guidance.</P>
        </Section>

        <Section title="How it works" accent={theme.slateDark}>
          {[["01 Describe", "Type how you are feeling in plain, everyday language."], ["02 Converse", "CareNav asks a few focused follow-up questions about duration and severity."], ["03 Assess", "Your symptoms are matched against a 60-condition clinical framework."], ["04 Act", "You receive clear guidance — self-care, pharmacy, GP, 111, or A&E."]].map(([t, d]) => (
            <div key={t} style={{ display: "flex", gap: "0.95rem", marginBottom: "0.9rem", alignItems: "flex-start" }}>
              <div style={{ width: "30px", height: "30px", borderRadius: "7px", background: theme.bgDeep, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${theme.border}` }}>
                <span style={{ fontFamily: theme.fontDisplay, fontSize: "0.65rem", color: theme.clay, fontWeight: "500" }}>{t.split(" ")[0]}</span>
              </div>
              <div>
                <p style={{ fontWeight: "700", color: theme.text, margin: "0 0 0.18rem", fontSize: "0.88rem" }}>{t.split(" ").slice(1).join(" ")}</p>
                <p style={{ color: theme.textMid, margin: 0, fontSize: "0.83rem", lineHeight: 1.6 }}>{d}</p>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Technology">
          <Bullets items={["React — front-end user interface", "Node.js and Express.js — back-end REST API", "Google Gemini AI (gemini-2.5-flash) — natural language understanding", "NHS ODS FHIR R4 API — NHS organisation lookup (production access approved)", "NHS Website Content API — condition data (integration pending)", "Vercel and Render.com — cloud deployment"]} />
        </Section>

        <Section title="Project Information" accent={theme.textLight}>
          <P>CareNav was developed as part of the CO6008 Implementation Project at the University of Chester (2025–2026). It is a prototype academic system and is not deployed in a live clinical environment.</P>
        </Section>
      </div>
      <Footer />
    </div>
  );
}

// ── PRIVACY ───────────────────────────────────────────────────────────────────
export function PrivacyPage() {
  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav />
      <PageHeader eyebrow="Legal" title="Privacy Policy" subtitle="Last updated January 2026. CareNav collects no personal data." />
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "2.5rem 2rem" }}>
        <Section title="Overview">
          <P>CareNav is committed to protecting your privacy. This policy explains what information is collected, how it is used, and your rights. CareNav is an academic prototype and is not operated by NHS England.</P>
        </Section>
        <Section title="What Data We Collect">
          <P>CareNav is designed with data minimisation as a core principle:</P>
          <Bullets items={["Symptom inputs are sent to Google Gemini AI for processing. They are not stored or retained by CareNav after your session ends.", "Location data (if you use the A&E feature) is used only to find nearby hospitals and is immediately discarded.", "No personal data — no name, date of birth, NHS number, email, or phone number is collected.", "No accounts required. No tracking cookies used."]} />
        </Section>
        <Section title="How Your Data is Used">
          <Bullets items={["Symptom descriptions are sent to Google Gemini AI for natural language understanding only. Google's privacy policy governs this processing.", "Location coordinates are used only for the A&E waiting times query and discarded immediately.", "No data is stored in any database or log file after your session ends."]} />
        </Section>
        <Section title="Third Party Services">
          <Bullets items={["Google Gemini AI — natural language processing. See policies.google.com/privacy.", "NHS ODS FHIR R4 API — NHS organisation data. Operated by NHS England.", "Vercel — front-end hosting. See vercel.com/legal/privacy-policy.", "Render.com — back-end API hosting. See render.com/privacy."]} />
        </Section>
        <Section title="Your Rights" accent={theme.textLight}>
          <P>As CareNav stores no personal data, there is nothing held about you that can be accessed, corrected, or deleted. Questions? Use the <a href="/contact" style={{ color: theme.clay }}>contact form</a>.</P>
        </Section>
      </div>
      <Footer />
    </div>
  );
}

// ── ACCESSIBILITY ─────────────────────────────────────────────────────────────
export function AccessibilityPage() {
  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav />
      <PageHeader eyebrow="Accessibility" title="Accessibility Statement" subtitle="Last updated January 2026. We are committed to making CareNav usable by everyone." />
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "2.5rem 2rem" }}>
        <Section title="Our Commitment">
          <P>CareNav is committed to making its website accessible to as many people as possible, including those with visual, motor, auditory, or cognitive disabilities. We aim to meet WCAG 2.1 AA standards as best practice.</P>
        </Section>
        <Section title="Accessibility Features">
          <Bullets items={["High contrast text — all body text meets WCAG AA contrast requirements.", "Responsive design — works on all screen sizes including mobile, tablet, and desktop.", "Chat widget expands to full screen on mobile devices for easier use.", "Plain English — all content is written in clear, jargon-free language.", "Semantic HTML with proper heading structures for screen reader navigation.", "All interactive elements are keyboard navigable.", "No flashing or rapidly changing visual content.", "Playfair Display and DM Sans chosen for high legibility at all sizes."]} />
        </Section>
        <Section title="Known Limitations" accent={theme.amber}>
          <Bullets items={["Some colour-coded indicators rely partly on colour. Text labels are also provided alongside all colour indicators.", "The chat widget text input does not yet have an explicit ARIA label.", "Some footer links are implemented as anchor elements and may vary in screen reader announcement."]} color={theme.amber} />
          <P>If you experience any accessibility issue, please <a href="/contact" style={{ color: theme.clay }}>contact us</a>.</P>
        </Section>
        <Section title="Browser Compatibility">
          <Bullets items={["Google Chrome (latest)", "Safari (latest — macOS and iOS)", "Mozilla Firefox (latest)", "Microsoft Edge (latest)"]} />
        </Section>
      </div>
      <Footer />
    </div>
  );
}

// ── CONTACT ───────────────────────────────────────────────────────────────────
export function ContactPage() {
  const [form, setForm]           = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr]             = useState(null);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { setErr("Please fill in all required fields."); return; }
    setSubmitted(true); setErr(null);
  };

  const inputStyle = {
    width: "100%", border: `1px solid ${theme.border}`, borderRadius: theme.radiusSm,
    padding: "0.62rem 0.85rem", fontSize: "0.88rem", fontFamily: theme.fontBody,
    outline: "none", background: theme.surface, color: theme.text, boxSizing: "border-box",
    transition: "border-color 0.15s",
  };

  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav />
      <PageHeader eyebrow="Get in touch" title="Contact Us" subtitle="Have a question, accessibility concern, or feedback about CareNav?" />
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "2.5rem 2rem" }}>

        <RedBox>
          <p style={{ color: theme.red, fontSize: "0.83rem", margin: 0, fontWeight: "600" }}>
            🚨 This is not a medical helpline. Medical emergency? Call <strong>999</strong>. Non-emergency medical advice? Call <strong>NHS 111</strong>.
          </p>
        </RedBox>

        {submitted ? (
          <div style={{ background: theme.greenLight, borderRadius: theme.radius, padding: "2.5rem", textAlign: "center", border: `1px solid ${theme.green}44` }}>
            <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: theme.green, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem", fontSize: "1.1rem" }}>✓</div>
            <h2 style={{ fontFamily: theme.fontDisplay, color: theme.green, fontWeight: "500", margin: "0 0 0.5rem" }}>Message sent</h2>
            <p style={{ color: theme.green, fontSize: "0.87rem", margin: 0 }}>Thank you. We will aim to respond within 5 working days.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "1.4rem" }}>

            {/* Form */}
            <div style={{ background: theme.surface, borderRadius: theme.radius, padding: "1.7rem", border: `1px solid ${theme.border}`, borderTop: `3px solid ${theme.clay}`, boxShadow: theme.shadow }}>
              <h2 style={{ fontSize: "0.7rem", fontWeight: "700", color: theme.clay, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 1.4rem" }}>Send a message</h2>

              {err && <div style={{ background: theme.redLight, borderRadius: theme.radiusSm, padding: "0.7rem 0.9rem", marginBottom: "1rem", color: theme.red, fontSize: "0.83rem" }}>{err}</div>}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem", marginBottom: "0.9rem" }}>
                {[["Full name *", "name", "text", "Your name"], ["Email *", "email", "email", "your@email.com"]].map(([label, name, type, ph]) => (
                  <div key={name}>
                    <label style={{ display: "block", fontSize: "0.75rem", fontWeight: "600", color: theme.text, marginBottom: "0.38rem" }}>{label}</label>
                    <input type={type} name={name} value={form[name]} onChange={e => setForm({ ...form, [e.target.name]: e.target.value })} placeholder={ph} style={inputStyle}
                      onFocus={e => e.currentTarget.style.borderColor = theme.clay}
                      onBlur={e => e.currentTarget.style.borderColor = theme.border}
                    />
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: "0.9rem" }}>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: "600", color: theme.text, marginBottom: "0.38rem" }}>Subject</label>
                <select name="subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="">Select a subject</option>
                  {["General enquiry", "Accessibility issue", "Privacy concern", "Technical issue", "Feedback", "Other"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: "1.4rem" }}>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: "600", color: theme.text, marginBottom: "0.38rem" }}>Message *</label>
                <textarea name="message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Describe your question or feedback..." rows={5} style={{ ...inputStyle, resize: "vertical" }}
                  onFocus={e => e.currentTarget.style.borderColor = theme.clay}
                  onBlur={e => e.currentTarget.style.borderColor = theme.border}
                />
              </div>

              <button onClick={submit} style={{ background: theme.clay, color: "#fff", border: "none", padding: "0.68rem 1.6rem", borderRadius: theme.radiusPill, fontFamily: theme.fontBody, fontWeight: "700", fontSize: "0.87rem", cursor: "pointer", transition: "all 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = theme.clayDark}
                onMouseLeave={e => e.currentTarget.style.background = theme.clay}
              >Send message</button>
            </div>

            {/* Info cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              {[
                { t: "Medical emergencies", b: "Call 999 immediately. Do not use this form.", bg: theme.redLight, border: theme.red + "33", color: theme.red },
                { t: "NHS 111", b: "For non-emergency medical advice, call 111 or visit 111.nhs.uk.", bg: theme.navyLight, border: theme.navy + "22", color: theme.navy },
                { t: "Response time", b: "We aim to respond to all enquiries within 5 working days.", bg: theme.greenLight, border: theme.green + "33", color: theme.green },
              ].map(({ t, b, bg, border, color }) => (
                <div key={t} style={{ background: bg, borderRadius: theme.radius, padding: "1.1rem 1.2rem", border: `1px solid ${border}` }}>
                  <p style={{ fontWeight: "700", color, margin: "0 0 0.3rem", fontSize: "0.83rem" }}>{t}</p>
                  <p style={{ color, margin: 0, fontSize: "0.79rem", lineHeight: 1.6 }}>{b}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

// ── TERMS ─────────────────────────────────────────────────────────────────────
export function TermsPage() {
  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav />
      <PageHeader eyebrow="Legal" title="Terms of Use" subtitle="Last updated January 2026. Please read before using CareNav." />
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "2.5rem 2rem" }}>

        <RedBox>
          <h2 style={{ fontSize: "0.7rem", fontWeight: "700", color: theme.red, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 0.65rem" }}>🚨 Important</h2>
          <P>CareNav is <strong>not a medical service</strong> and is <strong>not a replacement for professional medical advice</strong>. It does not diagnose, prescribe, or provide emergency care. Life-threatening emergency? Call <strong>999</strong> immediately.</P>
        </RedBox>

        <Section title="1. Acceptance of Terms">
          <P>By using CareNav you accept these Terms of Use. CareNav is an academic prototype from the University of Chester. It is not a commercial product and is not operated by NHS England.</P>
        </Section>
        <Section title="2. Nature of the Service">
          <P>CareNav provides general health information for non-emergency symptoms only. It does not diagnose conditions, provide clinical advice, prescribe medications, replace healthcare professionals, or provide emergency assistance.</P>
        </Section>
        <Section title="3. Medical Disclaimer">
          <P>All content is for general informational purposes only and is not tailored to your specific medical history, medications, allergies, or individual circumstances. Always consult a qualified healthcare professional before making any health decisions. CareNav accepts no liability for any loss, damage, or harm arising from reliance on this service.</P>
        </Section>
        <Section title="4. AI-Generated Content">
          <P>CareNav uses Google Gemini AI for conversational symptom collection only. All clinical guidance displayed is pre-written and reviewed content — the AI does not generate medical advice. Despite these safeguards, AI systems can produce unexpected outputs. CareNav does not guarantee accuracy.</P>
        </Section>
        <Section title="5. Limitation of Liability" accent={theme.amber}>
          <P>To the maximum extent permitted by law, CareNav, the University of Chester, and the project developer accept no liability for any inaccuracy in information provided, decisions made based on CareNav guidance, delays in seeking medical attention, or technical failure of the service.</P>
        </Section>
        <Section title="6. Intellectual Property">
          <P>All content, design, and code within CareNav is the intellectual property of the project developer and the University of Chester. NHS branding is used for academic purposes only and does not imply NHS affiliation or endorsement.</P>
        </Section>
        <Section title="7. Governing Law" accent={theme.textLight}>
          <P>These terms are governed by the laws of England and Wales. Questions? Use the <a href="/contact" style={{ color: theme.clay }}>contact form</a>.</P>
        </Section>
      </div>
      <Footer />
    </div>
  );
}